import React, { useEffect, useRef, useState } from 'react'
import { Button, Tooltip } from 'antd'
import { PhoneOutlined } from '@ant-design/icons'
import { useShallow } from 'zustand/shallow'
import { globalStore, t3cStore } from '@store'

interface CallingIndicatorProps {
  customerId: string
  drawerOpen: boolean
}

/** 渐出动画时长（ms），需与 .ai-calling-edge--exiting 的动画时长保持一致 */
const EXIT_DURATION = 400
/** 未查看抽屉的通话结束后，红色提示的停留时长（ms） */
const ENDED_HOLD = 1000

const CallingIndicator: React.FC<CallingIndicatorProps> = ({ customerId, drawerOpen }) => {
  const { callStatus, sessionId } = t3cStore(useShallow(s => ({
    callStatus: s.callStatus,
    sessionId: s.sessionId,
  })))
  const callActive = callStatus === 'calling' || callStatus === 'talking'
  /** 对方已接听：只有接通后才自动弹出抽屉，振铃阶段仅展示提示由用户自行点击 */
  const callAccepted = callStatus === 'talking'
  /** 抽屉打开时收起提示；通话进行中才展示 */
  const active = !drawerOpen && callActive

  const lastCallRef = useRef<{
    sessionId: string
    viewed: boolean
  } | null>(null)
  /** 本通电话结束且未查看抽屉：收起前先停留红色 */
  const pendingRedRef = useRef(false)
  /** 本通电话是否已处理过自动弹出（已弹过或用户已手动打开），避免手动关闭后又被弹回来 */
  const autoOpenHandledRef = useRef(false)
  /** 未查看抽屉的通话刚结束：先停留红色，再渐出 */
  const [redLeaving, setRedLeaving] = useState(false)
  /** 渐出期间保持挂载，动画播完再卸载 */
  const [rendered, setRendered] = useState(false)
  const [exiting, setExiting] = useState(false)

  // 维护「本通电话是否打开过抽屉」，并在通话结束时决定收起前是否需要红色提醒
  useEffect(() => {
    const previous = lastCallRef.current
    if (callActive) {
      const newSession = sessionId && previous?.sessionId && sessionId !== previous.sessionId
      if (!previous || newSession) {
        lastCallRef.current = { sessionId, viewed: drawerOpen }
        // 新的一通电话：重新允许接通后自动弹出
        autoOpenHandledRef.current = drawerOpen
      } else {
        if (sessionId) previous.sessionId = sessionId
        if (drawerOpen) previous.viewed = true
      }
      if (drawerOpen) autoOpenHandledRef.current = true
      pendingRedRef.current = false
      setRedLeaving(false)
      return
    }
    if (!previous) return
    pendingRedRef.current = !previous.viewed && !drawerOpen
    setRedLeaving(pendingRedRef.current)
    lastCallRef.current = null
    autoOpenHandledRef.current = false
  }, [callActive, sessionId, drawerOpen])

  // 对方接听后自动弹出抽屉：振铃/呼出中不主动打扰，坐席可自行点击提示提前打开。
  // 每通电话仅自动弹出一次，用户手动关闭后不再自动弹回。
  useEffect(() => {
    if (!callAccepted || drawerOpen || autoOpenHandledRef.current) return
    autoOpenHandledRef.current = true
    if (lastCallRef.current) lastCallRef.current.viewed = true
    globalStore.getState().setAiCallingDrawer({ customerId, open: true })
  }, [callAccepted, sessionId, drawerOpen, customerId])

  // 展示 / 收起：进入即渐入；收起先按需停留红色，再渐出，动画结束后卸载
  useEffect(() => {
    if (active) {
      pendingRedRef.current = false
      setRendered(true)
      setExiting(false)
      return
    }
    if (!rendered) return
    const hold = pendingRedRef.current ? ENDED_HOLD : 0
    const exitTimer = window.setTimeout(() => setExiting(true), hold)
    const unmountTimer = window.setTimeout(() => {
      pendingRedRef.current = false
      setRendered(false)
      setExiting(false)
      setRedLeaving(false)
    }, hold + EXIT_DURATION)
    return () => {
      window.clearTimeout(exitTimer)
      window.clearTimeout(unmountTimer)
    }
  }, [active, rendered])

  if (!rendered) return null

  const openDrawer = () => {
    autoOpenHandledRef.current = true
    if (lastCallRef.current) lastCallRef.current.viewed = true
    globalStore.getState().setAiCallingDrawer({ customerId, open: true })
  }

  const className = [
    'ai-calling-edge',
    redLeaving ? 'ai-calling-edge--ended' : '',
    exiting ? 'ai-calling-edge--exiting' : '',
  ].filter(Boolean).join(' ')

  return (
    <Tooltip title="点击打开AI智能通话" placement="left" mouseEnterDelay={0}>
      <Button
        className={className}
        type="text"
        aria-label="点击打开AI智能通话"
        aria-haspopup="dialog"
        onClick={openDrawer}
      >
        {/* 必须用非 span 容器包裹：antd 的 .ant-btn:active > span 会把按钮直接子 span
            改成 position: relative，导致绝对定位的圆弧与图标在按下瞬间脱离按钮定位 */}
        <div className="ai-calling-edge__graphic" aria-hidden="true">
          <span className="ai-calling-edge__arc" />
          <PhoneOutlined className="ai-calling-edge__icon" />
        </div>
      </Button>
    </Tooltip>
  )
}

export default CallingIndicator
